import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import overlayFactory from 'react-bootstrap-table2-overlay';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import { CustomSearchBox } from '../CustomControls/CustomSearchBox';
import { useUsersPicker } from '../../containers/Users/Hooks';

/**
 * Dual-panel transfer list para asignar usuarios a un centro médico.
 *
 * Panel izquierdo: usuarios disponibles con búsqueda + paginación server-side.
 *   - Click en fila → mueve al panel derecho.
 *   - Filas ya asignadas se muestran deshabilitadas.
 *
 * Panel derecho: usuarios asignados, lista simple.
 *   - Botón × por fila → devuelve usuario al panel izquierdo.
 *
 * Props:
 *   initialSelectedUsers  [{ userId, fullName, userName }]  — usuarios pre-seleccionados (edición)
 *   onSelectionChange     (userId: string[]) => void        — callback con array de IDs
 */
export const UserTransferList = ({ initialSelectedUsers, onSelectionChange, tenantId = null }) => {
    const { t } = useTranslation();
    const [query, setQuery] = useState({ q: '', pageSize: 8, pageIndex: 1, tenantId });
    const [selectedUsers, setSelectedUsers] = useState(initialSelectedUsers ?? []);

    const [availableUsers, isLoading, totalSize] = useUsersPicker(query);

    const selectedIds = selectedUsers.map(u => u.userId);

    const handleSearch = (q) => {
        setQuery(prev => ({ ...prev, q, pageIndex: 1 }));
    };

    const handleTableChange = (type, { page, sizePerPage }) => {
        if (type === 'pagination') {
            setQuery(prev => ({ ...prev, pageIndex: page, pageSize: sizePerPage }));
        }
    };

    const addUser = (user) => {
        if (selectedIds.includes(user.userId)) return;
        const updated = [...selectedUsers, user];
        setSelectedUsers(updated);
        onSelectionChange(updated.map(u => u.userId));
    };

    const removeUser = (userId) => {
        const updated = selectedUsers.filter(u => u.userId !== userId);
        setSelectedUsers(updated);
        onSelectionChange(updated.map(u => u.userId));
    };

    const rowStyle = (row) => {
        if (selectedIds.includes(row.userId)) {
            return { color: '#aaa', cursor: 'not-allowed', backgroundColor: '#f9f9f9' };
        }
        return { cursor: 'pointer' };
    };

    const rowEvents = {
        onClick: (e, row) => {
            if (!selectedIds.includes(row.userId)) {
                addUser(row);
            }
        },
    };

    const columns = [
        {
            dataField: 'fullName',
            text: t('security.users.fullName', 'Nombre'),
        },
        {
            dataField: 'userName',
            text: t('security.users.userName', 'Usuario'),
        },
        {
            dataField: 'userId',
            text: '',
            isDummyField: true,
            headerStyle: { width: '32px' },
            formatter: (cell, row) => {
                if (selectedIds.includes(row.userId)) {
                    return <span title={t('medicalCenter.alreadyAssigned', 'Ya asignado')} style={{ color: '#aaa' }}>✓</span>;
                }
                return <span style={{ color: '#5bc0de' }} title={t('medicalCenter.clickToAdd', 'Click para agregar')}>+</span>;
            },
        },
    ];

    return (
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>

            {/* ── Panel izquierdo: disponibles ── */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div className="card" style={{ marginBottom: 0 }}>
                    <div className="header" style={{ paddingBottom: 0 }}>
                        <h5 className="title" style={{ marginBottom: 4 }}>
                            {t('medicalCenter.availableUsers', 'Usuarios disponibles')}
                        </h5>
                        <CustomSearchBox
                            handleSearch={handleSearch}
                            placeholder={t('medicalCenter.searchUsers', 'Buscar usuario...')}
                        />
                    </div>
                    <div className="content" style={{ paddingTop: 0 }}>
                        <BootstrapTable
                            bordered={false}
                            hover
                            remote
                            keyField="userId"
                            classes="table-spaced table-spaced-small"
                            wrapperClasses="table-responsive"
                            data={availableUsers ?? []}
                            columns={columns}
                            rowStyle={rowStyle}
                            rowEvents={rowEvents}
                            noDataIndication={() => <>{t('commons.noData')}</>}
                            pagination={paginationFactory({
                                page: query.pageIndex,
                                sizePerPage: query.pageSize,
                                totalSize,
                                hideSizePerPage: true,
                            })}
                            onTableChange={handleTableChange}
                            loading={isLoading}
                            overlay={overlayFactory({ spinner: true })}
                        />
                    </div>
                </div>
            </div>

            {/* ── Panel derecho: asignados ── */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div className="card" style={{ marginBottom: 0, minHeight: 200 }}>
                    <div className="header">
                        <h5 className="title">
                            {t('medicalCenter.assignedUsers', 'Usuarios asignados')}{' '}
                            <span className="badge" style={{ backgroundColor: '#5bc0de' }}>
                                {selectedUsers.length}
                            </span>
                        </h5>
                    </div>
                    <div className="content" style={{ paddingTop: 0 }}>
                        {selectedUsers.length === 0 ? (
                            <p className="text-muted" style={{ padding: '8px 0', fontSize: 13 }}>
                                {t('medicalCenter.noAssignedUsers', 'Ningún usuario asignado. Hacé click en un usuario del panel izquierdo para agregarlo.')}
                            </p>
                        ) : (
                            <table className="table table-hover" style={{ marginBottom: 0 }}>
                                <tbody>
                                    {selectedUsers.map(user => (
                                        <tr key={user.userId}>
                                            <td style={{ verticalAlign: 'middle' }}>
                                                <div style={{ fontWeight: 500, lineHeight: 1.2 }}>{user.fullName}</div>
                                                <div style={{ fontSize: 12, color: '#888' }}>{user.userName}</div>
                                            </td>
                                            <td style={{ width: 36, verticalAlign: 'middle', textAlign: 'right' }}>
                                                <button
                                                    type="button"
                                                    className="btn btn-xs btn-default"
                                                    title={t('medicalCenter.removeUser', 'Quitar usuario')}
                                                    onClick={() => removeUser(user.userId)}
                                                    style={{ lineHeight: 1, padding: '2px 7px' }}
                                                >
                                                    &times;
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

        </div>
    );
};

UserTransferList.propTypes = {
    initialSelectedUsers: PropTypes.arrayOf(PropTypes.shape({
        userId: PropTypes.string.isRequired,
        fullName: PropTypes.string,
        userName: PropTypes.string,
    })),
    onSelectionChange: PropTypes.func.isRequired,
};

UserTransferList.defaultProps = {
    initialSelectedUsers: [],
};
